import React, { useState, useEffect } from "react";
import axios from "axios";
import { config } from "../../../Helpers/env";
import { LoadingGif } from "../../Utils/Loader";
import MainSnackbar from "../../Utils/Snackbar/SmsSnackbar";
import { Container } from "../../Utils/styledComponents";
import PageTitle from "../../Utils/smsTitle";
import { SubHeader } from "../../../Containers/SubHeader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStethoscope,
  faFileExcel,
  faFilePdf,
  faDollarSign,
  faCoins,
  faUserMd,
  faHospital,
  faCalendarAlt,
  faCreditCard,
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as snackbarActions from "../../../actions/snackbarActions";
import { BackButton } from "../../Utils/Buttons/DataExportationButton";

const AllServicesReport = ({ actions }) => {
  const today = new Date().toISOString().slice(0, 10);
  const firstDayOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1
  )
    .toISOString()
    .slice(0, 10);

  const [filters, setFilters] = useState({
    start_date: firstDayOfMonth,
    end_date: today,
    search: "",
    page: 1,
    per_page: 50
  });

  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const perPageOptions = [50, 100, 200, 500];

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.search.trim()) params.append("search", filters.search.trim());
      params.append("page", filters.page);
      params.append("per_page", filters.per_page);

      const res = await axios.get(
        `${config.smsUrl}/creport/all_services_report_v2`,
        { params }
      );
      setReport(res.data);
    } catch (err) {
      actions.snackbarActions.errorSnackbar?.("Failed to load report");
      console.error(err);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      setFilters((prev) => ({ ...prev, page: 1 }));
      fetchReport();
    }, 500);
    return () => clearTimeout(delay);
  }, [filters.start_date, filters.end_date, filters.search]);

  useEffect(() => {
    fetchReport();
  }, [filters.page, filters.per_page]);

  const handleDownload = async (format) => {
    try {
      const response = await axios.get(
        `${config.smsUrl}/creport/all_services_report_v2_downloads`,
        {
          params: { ...filters, format },
          responseType: "blob"
        }
      );

      const blob = new Blob([response.data], {
        type:
          format === "pdf"
            ? "application/pdf"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `All_Services_Report.${format === "pdf" ? "pdf" : "xlsx"}`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const { summary = {}, data = [], pagination = {} } = report || {};

  const formatSSP = (amount) =>
    `SSP ${Number(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0
    })}`;
  const formatUSD = (amount) =>
    `$${Number(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;

  const getVisitLink = (row) => {
    if (row.payment_type === "INSURANCE") {
      return `/invoice-details/${row.visit_id}/${row.provider_id || 0}/${
        row.claim_status || 0
      }/${row.provider_type || "private"}`;
    }
    if (row.visit_type === "1") return `/billingreceipt/${row.visit_id}`;
    if (row.visit_type === "2") {
      const admissionId =
        row.admission_id && row.admission_id.trim()
          ? row.admission_id
          : row.visit_id;
      return `/viewinpatientbill/${row.visit_id}/${admissionId}/1`;
    }
    return `/billingreceipt/${row.visit_id}`;
  };

  const handleVisitClick = (row) => {
    window.open(getVisitLink(row), "_blank");
  };

  return (
    <>
      <PageTitle title="All Services Report" />
      <SubHeader title="Reports" subTitle="Service Revenue • All Transactions">
        <FontAwesomeIcon icon={faStethoscope} />
      </SubHeader>

      <Container className="mt-1">
        <MainSnackbar />

        {/* ===== TOP SECTION ===== */}
        <div style={{ padding: "8px 0 0 0" }}>
          {/* Back + Export */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              marginBottom: "12px",
              gap: "8px"
            }}
          >
            <BackButton to="/reports/generalreports" text="Reports" />

            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={() => handleDownload("excel")}
                className="btn btn-sm text-white"
                style={{
                  backgroundColor: "#389081",
                  borderColor: "#389081",
                  height: "32px",
                  padding: "0 14px",
                  fontSize: "13px",
                  borderRadius: "6px"
                }}
              >
                <FontAwesomeIcon icon={faFileExcel} className="me-1" />
                Excel
              </button>
              <button
                type="button"
                onClick={() => handleDownload("pdf")}
                className="btn btn-sm text-white"
                style={{
                  backgroundColor: "#389081",
                  borderColor: "#389081",
                  height: "32px",
                  padding: "0 14px",
                  fontSize: "13px",
                  borderRadius: "6px"
                }}
              >
                <FontAwesomeIcon icon={faFilePdf} className="me-1" />
                PDF
              </button>
            </div>
          </div>

          {/* Filters – tight */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "10px",
              marginBottom: "14px"
            }}
          >
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.start_date}
              onChange={(e) =>
                setFilters({ ...filters, start_date: e.target.value })
              }
              style={{
                width: "145px",
                height: "32px",
                padding: "2px 8px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.end_date}
              onChange={(e) =>
                setFilters({ ...filters, end_date: e.target.value })
              }
              style={{
                width: "145px",
                height: "32px",
                padding: "2px 8px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search service, patient, visit..."
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              style={{
                minWidth: "200px",
                height: "32px",
                padding: "2px 8px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />
            <select
              className="form-select form-select-sm"
              value={filters.per_page}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  per_page: parseInt(e.target.value),
                  page: 1
                })
              }
              style={{
                width: "110px",
                height: "32px",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            >
              {perPageOptions.map((n) => (
                <option key={n} value={n}>
                  {n} rows
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary Cards – system colors */}
        <div className="row g-3 mb-4">
          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm text-white"
              style={{
                background: "linear-gradient(135deg, #389081, #027a5f)",
                borderRadius: "10px"
              }}
            >
              <div className="card-body text-center py-3">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="mb-1 opacity-75"
                />
                <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
                  Total Transactions
                </h6>
                <h3 className="mb-0 fw-bold" style={{ fontSize: "1.35rem" }}>
                  {summary.total_transactions || 0}
                </h3>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm text-white"
              style={{
                background: "linear-gradient(135deg, #08819c, #0a6b7a)",
                borderRadius: "10px"
              }}
            >
              <div className="card-body text-center py-3">
                <FontAwesomeIcon icon={faCoins} className="mb-1 opacity-75" />
                <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
                  Revenue (SSP)
                </h6>
                <h3 className="mb-0 fw-bold" style={{ fontSize: "1.2rem" }}>
                  {formatSSP(summary.total_revenue_ssp)}
                </h3>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm text-white"
              style={{
                background: "linear-gradient(135deg, #F76B1D, #e05a0d)",
                borderRadius: "10px"
              }}
            >
              <div className="card-body text-center py-3">
                <FontAwesomeIcon
                  icon={faDollarSign}
                  className="mb-1 opacity-75"
                />
                <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
                  Revenue (USD)
                </h6>
                <h3 className="mb-0 fw-bold" style={{ fontSize: "1.2rem" }}>
                  {formatUSD(summary.total_revenue_usd)}
                </h3>
              </div>
            </div>
          </div>

          <div className="col-6 col-md-3">
            <div
              className="card border-0 shadow-sm text-white"
              style={{
                background: "linear-gradient(135deg, #3e3e3e, #2b2a2a)",
                borderRadius: "10px"
              }}
            >
              <div className="card-body text-center py-3">
                <FontAwesomeIcon
                  icon={faCreditCard}
                  className="mb-1 opacity-75"
                />
                <h6 className="mb-1 opacity-75" style={{ fontSize: "12px" }}>
                  Payment Split
                </h6>
                <div style={{ fontSize: "13px" }}>
                  <strong>{summary.cash_count || 0}</strong> Cash ·{" "}
                  <strong>{summary.insurance_count || 0}</strong> Insurance
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* TABLE */}
        {loading ? (
          <div className="text-center py-5">
            <LoadingGif />
          </div>
        ) : data.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <h5>No services found for the selected period</h5>
            <small>Try adjusting your filters</small>
          </div>
        ) : (
          <>
            <div
              className="table-responsive bg-white"
              style={{
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid #e0e0e0"
              }}
            >
              <table className="table table-hover align-middle mb-0">
                <thead style={{ backgroundColor: "#e8f5f3" }}>
                  <tr>
                    <th style={{ fontSize: "13px", fontWeight: 600 }}>#</th>
                    <th style={{ fontSize: "13px", fontWeight: 600 }}>Date</th>
                    <th style={{ fontSize: "13px", fontWeight: 600 }}>
                      Visit ID
                    </th>
                    <th style={{ fontSize: "13px", fontWeight: 600 }}>
                      Patient
                    </th>
                    <th style={{ fontSize: "13px", fontWeight: 600 }}>
                      Service
                    </th>
                    <th style={{ fontSize: "13px", fontWeight: 600 }}>
                      Department
                    </th>
                    <th
                      className="text-center"
                      style={{ fontSize: "13px", fontWeight: 600 }}
                    >
                      Qty
                    </th>
                    <th
                      className="text-end"
                      style={{ fontSize: "13px", fontWeight: 600 }}
                    >
                      SSP
                    </th>
                    <th
                      className="text-end"
                      style={{ fontSize: "13px", fontWeight: 600 }}
                    >
                      USD
                    </th>
                    <th style={{ fontSize: "13px", fontWeight: 600 }}>
                      Payment
                    </th>
                    <th style={{ fontSize: "13px", fontWeight: 600 }}>
                      Insurer
                    </th>
                    <th style={{ fontSize: "13px", fontWeight: 600 }}>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, i) => (
                    <tr
                      key={i}
                      onClick={() => handleVisitClick(item)}
                      style={{ cursor: "pointer" }}
                    >
                      <td style={{ fontSize: "13px" }}>
                        {(filters.page - 1) * filters.per_page + i + 1}
                      </td>
                      <td style={{ fontSize: "13px" }}>
                        {new Date(item.sale_date).toLocaleDateString("en-GB")}
                      </td>
                      <td style={{ fontSize: "13px" }}>
                        <span
                          style={{
                            background: "#389081",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: 600
                          }}
                        >
                          {item.visit_id}
                        </span>
                      </td>
                      <td style={{ fontSize: "13px", fontWeight: 600 }}>
                        {item.patient_name}
                      </td>
                      <td style={{ fontSize: "13px" }}>
                        <strong>{item.service_name}</strong>
                      </td>
                      <td
                        style={{ fontSize: "13px" }}
                        className="text-muted"
                      >
                        {item.department_name}
                      </td>
                      <td
                        className="text-center"
                        style={{ fontSize: "13px" }}
                      >
                        {item.quantity}
                      </td>
                      <td
                        className="text-end"
                        style={{ fontSize: "13px", fontWeight: 600 }}
                      >
                        {formatSSP(item.revenue_ssp)}
                      </td>
                      <td
                        className="text-end"
                        style={{ fontSize: "13px", fontWeight: 600 }}
                      >
                        {item.revenue_usd > 0
                          ? formatUSD(item.revenue_usd)
                          : "-"}
                      </td>
                      <td style={{ fontSize: "13px" }}>
                        {item.payment_type === "CASH" ? (
                          <span
                            style={{
                              background: "#d4edda",
                              color: "#155724",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: 600
                            }}
                          >
                            CASH
                          </span>
                        ) : (
                          <span
                            style={{
                              background: "#cce5ff",
                              color: "#004085",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontSize: "12px",
                              fontWeight: 600
                            }}
                          >
                            INSURANCE
                          </span>
                        )}
                      </td>
                      <td style={{ fontSize: "12px" }}>
                        {item.insurance_provider || "-"}
                      </td>
                      <td style={{ fontSize: "13px" }}>
                        {item.patient_type === "Inpatient" ? (
                          <FontAwesomeIcon
                            icon={faHospital}
                            style={{ color: "#c0392b" }}
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faUserMd}
                            style={{ color: "#027a5f" }}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div
                className="d-flex justify-content-between align-items-center mt-3"
                style={{ fontSize: "13px" }}
              >
                <div className="text-muted">
                  Showing {(filters.page - 1) * filters.per_page + 1} to{" "}
                  {Math.min(
                    filters.page * filters.per_page,
                    pagination.total_records
                  )}{" "}
                  of {pagination.total_records} records
                </div>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button
                    className="btn btn-sm"
                    disabled={filters.page === 1}
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        page: prev.page - 1
                      }))
                    }
                    style={{
                      backgroundColor: "#389081",
                      color: "#fff",
                      borderRadius: "6px",
                      height: "30px",
                      padding: "0 12px"
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} /> Prev
                  </button>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "0 10px",
                      fontWeight: 600
                    }}
                  >
                    Page {filters.page} of {pagination.total_pages}
                  </span>
                  <button
                    className="btn btn-sm"
                    disabled={filters.page === pagination.total_pages}
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        page: prev.page + 1
                      }))
                    }
                    style={{
                      backgroundColor: "#389081",
                      color: "#fff",
                      borderRadius: "6px",
                      height: "30px",
                      padding: "0 12px"
                    }}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        <div
          className="text-center mt-4 text-muted"
          style={{ fontSize: "12px" }}
        >
          Generated: {report?.generated_at || "-"} | Period:{" "}
          {filters.start_date} — {filters.end_date}
        </div>
      </Container>
    </>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: { snackbarActions: bindActionCreators(snackbarActions, dispatch) }
});

export default connect(null, mapDispatchToProps)(AllServicesReport);